import { TooltipIcon } from "@solomon-labs/design/components/icons";
import {
    Text,
    Tooltip,
    TooltipContent,
    TooltipProvider,
    TooltipTrigger
} from "@solomon-labs/design/components/ui";
import React from "react";

import usdvLogoUrl from "../../../assets/logos/usdv-logomark.png";
import { FieldRow } from "../../../components/FieldRow";
import { NumberField } from "../../../components/NumberField";
import { Section } from "../../../components/Section";
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue
} from "../../../components/ui/select";
import { ToggleMenu, ToggleMenuItem } from "../../../components/ui/toggle-menu";

import { PERIOD_OPTIONS, type ProgramConfig } from "./types";

function formatProgramDuration(program: ProgramConfig): string {
    if (program.programType === "continuous") return "Continuous";
    const days =
        program.periodDays ??
        PERIOD_OPTIONS.find((p) => p.value === program.period)?.days;
    return days ? `${days} days` : program.period;
}

/** Whether the program's rewards are capped by a fixed budget. Defaults match
 *  the builder's fresh-draft default (Continuous · open budget), so an
 *  unspecified program reads as NOT fixed-budget. */
function isFixedBudget(program: ProgramConfig): boolean {
    return (
        (program.programType ?? "continuous") === "fixed" &&
        (program.budgetMode ?? "open") === "fixed"
    );
}

/** Read summary of the budget / APY slot. Fixed-budget programs show the
 *  Program Budget; a fixed base APY shows the base APY; the variable earned
 *  budget shows the User % (share of earnings passed to the user). */
function formatProgramReward(program: ProgramConfig): string {
    if (isFixedBudget(program)) {
        return `${(program.budget ?? 0).toLocaleString("en-US")} USDv`;
    }
    if ((program.baseApy ?? 0) > 0) {
        return `Base APY (Fixed) · ${program.baseApy}%`;
    }
    return `User ${program.userPct ?? 80}%`;
}

/** Read-summary label for the budget / APY slot. */
function rewardRowLabel(program: ProgramConfig): string {
    if (isFixedBudget(program)) return "Program Budget";
    return (program.baseApy ?? 0) > 0 ? "APY" : "Revenue Split";
}

function formatProgramSettlement(program: ProgramConfig): string {
    return `${program.settlementCycleLength ?? 30} ${program.settlementCycleUnit ?? "days"}`;
}

/** Placeholder Solomon-generated distribution address, shown in the External
 *  Funding line. The real value is auto-generated by Solomon per client (from
 *  the client's backend integration) — passed in as `distributionAddress`;
 *  this is the demo mock.
 *  BACKEND GAP — Solomon does not yet expose a per-client distribution address
 *  field; until it does, callers omit the prop and this mock renders. */
const DEFAULT_DISTRIBUTION_ADDRESS = "So1vDXm4…9f2a1111";

/**
 * Derived Funding line — Funding is NOT a stored choice; it falls out of the
 * Budget Type:
 *  - **Variable** (earned APY: not a fixed budget AND `apyMode === "earned"`) →
 *    rewards generated fund distributions.
 *  - **Fixed** (fixed budget OR a fixed Base APY — everything else) → the client
 *    sends the amount to distribute to its Solomon-generated address.
 */
function fundingDescription(isFixed: boolean, address: string): string {
    return isFixed
        ? `You send the amount to distribute to ${address}.`
        : "Any rewards generated are used for distributions.";
}

/** Read-summary Funding line — same derivation, from the settled `program`. */
function formatProgramFunding(program: ProgramConfig, address: string): string {
    // Fixed (external): a fixed budget, or a fixed base APY. Otherwise Variable.
    const isFixed = isFixedBudget(program) || (program.baseApy ?? 0) > 0;
    return fundingDescription(isFixed, address);
}

/** The controls a scripted sim can drive, in playthrough order. Each maps to
 *  one field in the draft form. */
export type ProgramConfigControl =
    | "program-type"
    // The Program Type ToggleMenu's other two options, exposed as their own
    // cursor targets so a scripted sim can travel to + click "Fixed Length,
    // Open Budget" or "Continuous" (not just the default "Fixed, Fixed").
    | "program-type-open"
    | "program-type-continuous"
    | "period"
    | "budget"
    | "apy-mode"
    | "base-apy"
    // The Variable-budget User % field (share of earnings passed to the user).
    | "user-pct"
    | "settlement";

/** Forced-interaction phase the cursor is painting on the active control. */
export type ProgramConfigDemoPhase = "hover" | "press";

/**
 * Demo hook for the cursor-driven sim (see {@link ProgramConfigurationSim}).
 * Production callers omit it → the form renders exactly as before. When set,
 * the named `control` paints its forced hover/focus VISUAL (CSS pseudo-states
 * can't be JS-driven) so a scripted cursor hovering/clicking it is believable.
 * Every control also carries a stable `data-sim-cursor-key` (its id) so the
 * cursor can target it; those attributes are inert in production.
 */
export interface ProgramConfigDemo {
    control: ProgramConfigControl | null;
    phase: ProgramConfigDemoPhase;
}

export interface ProgramConfigurationProps {
    program: ProgramConfig;
    /** Sim-only — drives forced hover/focus on one control at a time.
     *  Omit in production. */
    demo?: ProgramConfigDemo;
    /** Draft-config values + setters — lifted in ProgramDetail because they
     *  feed the BuilderConfig assembled at the confirm CTA. */
    programType: "fixed" | "continuous";
    setProgramType: (programType: "fixed" | "continuous") => void;
    budgetMode: "fixed" | "open";
    setBudgetMode: (budgetMode: "fixed" | "open") => void;
    /** Open-budget / continuous: variable earned APY vs a fixed base APY. */
    apyMode: "earned" | "base";
    setApyMode: (apyMode: "earned" | "base") => void;
    baseApy: number;
    setBaseApy: (baseApy: number) => void;
    /** Variable earned budget: the % of earnings passed to the user (the
     *  remainder is kept by the client). Shown as the "User %" field. `null`
     *  renders the field blank (a cleared mid-edit state). */
    userPct: number | null;
    setUserPct: (userPct: number) => void;
    periodDays: number;
    setPeriodDays: (periodDays: number) => void;
    /** `null` renders the field blank (a cleared mid-edit state). */
    settlementCycleLength: number | null;
    setSettlementCycleLength: (settlementCycleLength: number) => void;
    settlementCycleUnit: "days" | "weeks";
    setSettlementCycleUnit: (settlementCycleUnit: "days" | "weeks") => void;
    /** `null` renders the field blank (a cleared mid-edit state). */
    budget: number | null;
    setBudget: (budget: number) => void;
    /** The client's Solomon-generated distribution address, interpolated into
     *  the derived Funding line's Fixed (External) copy — the client sends the
     *  amount to distribute there. Auto-generated by Solomon per client; falls
     *  back to a demo mock. */
    distributionAddress?: string;
}

/**
 * ProgramConfiguration — the Configuration section of the program detail.
 * A Domain Component (DC) that derives its mode from `program.status`
 * (draft ⇒ creation, active/ended ⇒ created — the `isActive` gate) and
 * renders the matching body into a `<Section heading="Configuration">`:
 * the editable FieldRows form for a draft, the read-only summary rows
 * otherwise.
 *
 * The shell (`Section`, a GC) owns no mode; this DC owns it. The draft
 * config values/setters are props because they live in ProgramDetail and
 * feed the BuilderConfig assembled at the confirm CTA.
 *
 * Hoisted into `shared/widgets/yaas/programs` so external consumers (the
 * "+New Program" wiki doc) can render the bare Configuration section
 * fixture-driven, with no wallet / RPC / orgApiFetch. The app keeps a
 * re-export shim at the old path so `yaas-client` compiles unchanged.
 *
 * @see ProgramConfiguration.intent.md   policy
 * @see ProgramConfiguration.layout.html intended layout
 */
export function ProgramConfiguration({
    program,
    demo,
    programType,
    setProgramType,
    budgetMode,
    setBudgetMode,
    apyMode,
    setApyMode,
    baseApy,
    setBaseApy,
    userPct,
    setUserPct,
    periodDays,
    setPeriodDays,
    settlementCycleLength,
    setSettlementCycleLength,
    settlementCycleUnit,
    setSettlementCycleUnit,
    budget,
    setBudget,
    distributionAddress
}: ProgramConfigurationProps): React.ReactElement {
    // draft (in_progress) ⇒ editable form; active / ended ⇒ read summary.
    const creation = program.status === "in_progress";
    // Draft budget/APY fork: fixed-length + fixed-budget shows the Program
    // Budget; open-budget & continuous show the APY toggle group.
    const draftIsFixedBudget =
        programType === "fixed" && budgetMode === "fixed";
    // The address the derived Funding line interpolates for a Fixed (External)
    // program. Solomon-generated per client; demo mock when omitted.
    const fundingAddress = distributionAddress ?? DEFAULT_DISTRIBUTION_ADDRESS;
    // Derived Funding line for the DRAFT form. Variable = an earned-APY,
    // non-fixed budget; everything else (a fixed budget OR a fixed Base APY) is
    // Fixed → the External copy. Purely derived from Budget Type — never stored.
    const draftFundingLine = fundingDescription(
        draftIsFixedBudget || apyMode === "base",
        fundingAddress
    );

    // Sim demo helpers. `demoOn(id)` → the control the cursor is currently on.
    // For button-like controls (ToggleMenu items) hover/press map to the
    // Button's forceHover/forcePressed; for field controls (Select trigger /
    // NumberField) hover→forceHover and press→forceFocus (a click focuses the
    // field). All are no-ops in production (`demo` undefined).
    const demoOn = (id: ProgramConfigControl): boolean => demo?.control === id;
    const demoHover = (id: ProgramConfigControl): boolean =>
        demoOn(id) && demo?.phase === "hover";
    const demoPress = (id: ProgramConfigControl): boolean =>
        demoOn(id) && demo?.phase === "press";

    if (!creation) {
        // Read-only summary — the same FieldRow GC, flipped to `readOnly`.
        return (
            <Section heading="Configuration" className="pt-8" divider>
                <div className="px-4 pb-3 border-b border-line-base">
                    <div className="mt-5 max-w-2xl">
                        <FieldRow
                            readOnly
                            label="Program Duration"
                            value={formatProgramDuration(program)}
                        />
                        <FieldRow
                            readOnly
                            label={rewardRowLabel(program)}
                            value={formatProgramReward(program)}
                        />
                        {/* Funding — DERIVED from the Budget Type (not a stored
                            choice), grouped with the budget concept. */}
                        <FieldRow
                            readOnly
                            label="Funding"
                            value={formatProgramFunding(
                                program,
                                fundingAddress
                            )}
                        />
                        <FieldRow
                            readOnly
                            label="Distribution Schedule"
                            value={formatProgramSettlement(program)}
                        />
                    </div>
                </div>
            </Section>
        );
    }

    return (
        <Section heading="Configuration" className="pt-8" divider>
            <div className="px-4">
                <div className="flex flex-col gap-4 mt-6 max-w-2xl">
                    <FieldRow label="Program Type" controlClassName="shrink-0">
                        <ToggleMenu<"fixed_fixed" | "fixed_open" | "continuous">
                            value={
                                programType === "continuous"
                                    ? "continuous"
                                    : budgetMode === "open"
                                      ? "fixed_open"
                                      : "fixed_fixed"
                            }
                            onChange={(key) => {
                                if (key === "continuous") {
                                    setProgramType("continuous");
                                } else {
                                    setProgramType("fixed");
                                    setBudgetMode(
                                        key === "fixed_open" ? "open" : "fixed"
                                    );
                                }
                            }}
                            spacing={1}
                            className="flex-wrap"
                        >
                            {/* Continuous is the FIRST option and the default a
                                fresh draft opens on. Fixed Length variants
                                follow. */}
                            <ToggleMenuItem
                                value="continuous"
                                variant="toggle"
                                size="sm"
                                data-sim-cursor-key="cfg-program-type-continuous"
                                forceHover={demoHover(
                                    "program-type-continuous"
                                )}
                                forcePressed={demoPress(
                                    "program-type-continuous"
                                )}
                            >
                                Continuous
                            </ToggleMenuItem>
                            <ToggleMenuItem
                                value="fixed_fixed"
                                variant="toggle"
                                size="sm"
                                data-sim-cursor-key="cfg-program-type"
                                forceHover={demoHover("program-type")}
                                forcePressed={demoPress("program-type")}
                            >
                                Fixed Length, Fixed Budget
                            </ToggleMenuItem>
                            <ToggleMenuItem
                                value="fixed_open"
                                variant="toggle"
                                size="sm"
                                data-sim-cursor-key="cfg-program-type-open"
                                forceHover={demoHover("program-type-open")}
                                forcePressed={demoPress("program-type-open")}
                            >
                                Fixed Length, Open Budget
                            </ToggleMenuItem>
                        </ToggleMenu>
                    </FieldRow>
                    {/* Program Period — fixed-length shows the days input;
                        Continuous has no fixed end, so the row keeps its label
                        but reads "Continuous" in place of the input. */}
                    <FieldRow label="Program Period">
                        {programType === "fixed" ? (
                            <NumberField
                                data-sim-cursor-key="cfg-period"
                                forceHover={demoHover("period")}
                                forceFocus={demoPress("period")}
                                value={periodDays}
                                onChange={(n) =>
                                    setPeriodDays(n === null ? 0 : n)
                                }
                                ariaLabel="Program period in days"
                                adornment={
                                    <Text
                                        variant="small"
                                        render={<span />}
                                        className="text-foreground-neutral shrink-0"
                                    >
                                        days
                                    </Text>
                                }
                            />
                        ) : (
                            <div className="flex items-center h-8">
                                <Text
                                    variant="small"
                                    render={<span />}
                                    className="text-foreground-base"
                                >
                                    Continuous
                                </Text>
                            </div>
                        )}
                    </FieldRow>
                    {/* Budget Type — always shown. Fixed-budget is locked to
                        "Fixed" (static text); open-budget & continuous choose
                        Variable / Fixed. */}
                    {/* TODO(tooltip): add the Budget Type hint copy once product
                        provides it. */}
                    <FieldRow label="Budget Type" controlClassName="shrink-0">
                        {draftIsFixedBudget ? (
                            // Match the sm toggle's box height (text + py-1 +
                            // 1px border) so switching Fixed <-> Open budget
                            // keeps the Budget Type row the same height.
                            <div className="flex items-center py-1 border border-transparent">
                                <Text
                                    variant="small"
                                    render={<span />}
                                    className="text-foreground-base"
                                >
                                    Fixed
                                </Text>
                            </div>
                        ) : (
                            <ToggleMenu<"earned" | "base">
                                value={apyMode}
                                onChange={(key) =>
                                    setApyMode(
                                        key === "base" ? "base" : "earned"
                                    )
                                }
                                spacing={1}
                                className="flex-wrap"
                            >
                                <ToggleMenuItem
                                    value="earned"
                                    variant="toggle"
                                    size="sm"
                                    data-sim-cursor-key="cfg-apy-mode"
                                    forceHover={demoHover("apy-mode")}
                                    forcePressed={demoPress("apy-mode")}
                                >
                                    Variable
                                </ToggleMenuItem>
                                <ToggleMenuItem
                                    value="base"
                                    variant="toggle"
                                    size="sm"
                                >
                                    Fixed
                                </ToggleMenuItem>
                            </ToggleMenu>
                        )}
                    </FieldRow>
                    {/* Value — Program Budget for fixed-budget; Base APY
                        (% input when Fixed, static text when Variable) else. */}
                    {draftIsFixedBudget ? (
                        <FieldRow
                            label="Program Budget"
                            hint={
                                <TooltipProvider>
                                    <Tooltip>
                                        <TooltipTrigger
                                            render={
                                                <span className="inline-flex cursor-help text-foreground-neutral hover:text-foreground-base" />
                                            }
                                        >
                                            <TooltipIcon className="size-4" />
                                        </TooltipTrigger>
                                        <TooltipContent side="top">
                                            Total rewards budget for the
                                            program.
                                        </TooltipContent>
                                    </Tooltip>
                                </TooltipProvider>
                            }
                        >
                            <NumberField
                                data-sim-cursor-key="cfg-budget"
                                forceHover={demoHover("budget")}
                                forceFocus={demoPress("budget")}
                                value={budget}
                                onChange={(n) => setBudget(n === null ? 0 : n)}
                                ariaLabel="Program budget"
                                format={(v) => v.toLocaleString("en-US")}
                                adornment={
                                    <span className="flex items-center gap-1.5 shrink-0">
                                        <img
                                            src={usdvLogoUrl}
                                            alt=""
                                            className="size-4"
                                        />
                                        <Text
                                            variant="small"
                                            render={<span />}
                                            className="text-foreground-neutral"
                                        >
                                            USDv
                                        </Text>
                                    </span>
                                }
                            />
                        </FieldRow>
                    ) : apyMode === "base" ? (
                        // Fixed base APY — the % input, unchanged.
                        <FieldRow
                            label="Base APY"
                            hint={
                                <TooltipProvider>
                                    <Tooltip>
                                        <TooltipTrigger
                                            render={
                                                <span className="inline-flex cursor-help text-foreground-neutral hover:text-foreground-base" />
                                            }
                                        >
                                            <TooltipIcon className="size-4" />
                                        </TooltipTrigger>
                                        <TooltipContent side="top">
                                            Minimum APY distributed for Rewards
                                        </TooltipContent>
                                    </Tooltip>
                                </TooltipProvider>
                            }
                        >
                            <NumberField
                                data-sim-cursor-key="cfg-base-apy"
                                forceHover={demoHover("base-apy")}
                                forceFocus={demoPress("base-apy")}
                                value={baseApy}
                                onChange={(n) => setBaseApy(n === null ? 0 : n)}
                                ariaLabel="Base APY"
                                allowDecimal
                                adornment={
                                    <Text
                                        variant="small"
                                        render={<span />}
                                        className="text-foreground-neutral shrink-0"
                                    >
                                        %
                                    </Text>
                                }
                            />
                        </FieldRow>
                    ) : (
                        // Variable earned budget — the revenue split. "User %"
                        // is the share of earnings passed to the user; the
                        // remainder is kept by the client.
                        <FieldRow
                            label="User %"
                            hint={
                                <TooltipProvider>
                                    <Tooltip>
                                        <TooltipTrigger
                                            render={
                                                <span className="inline-flex cursor-help text-foreground-neutral hover:text-foreground-base" />
                                            }
                                        >
                                            <TooltipIcon className="size-4" />
                                        </TooltipTrigger>
                                        <TooltipContent side="top">
                                            Share of earnings passed to the
                                            user. The remainder is kept by the
                                            company recipient.
                                        </TooltipContent>
                                    </Tooltip>
                                </TooltipProvider>
                            }
                        >
                            <NumberField
                                data-sim-cursor-key="cfg-user-pct"
                                forceHover={demoHover("user-pct")}
                                forceFocus={demoPress("user-pct")}
                                value={userPct}
                                onChange={(n) =>
                                    setUserPct(
                                        n === null
                                            ? 0
                                            : Math.min(100, Math.max(0, n))
                                    )
                                }
                                ariaLabel="User percentage"
                                adornment={
                                    <Text
                                        variant="small"
                                        render={<span />}
                                        className="text-foreground-neutral shrink-0"
                                    >
                                        %
                                    </Text>
                                }
                            />
                        </FieldRow>
                    )}
                    {/* Funding — DERIVED from the Budget Type above (not a
                        separate choice), so it renders static text, not an
                        input: Variable earned budgets fund distributions from
                        generated rewards; Fixed budgets / Base APY send the
                        amount to the client's Solomon-generated address. */}
                    <FieldRow label="Funding" controlClassName="flex-1 min-w-0">
                        <div className="flex items-start min-h-8 py-1">
                            <Text
                                variant="small"
                                render={<span />}
                                className="text-foreground-base"
                            >
                                {draftFundingLine}
                            </Text>
                        </div>
                    </FieldRow>
                    <FieldRow label="Distribution Schedule">
                        <div className="flex items-center gap-2">
                            <NumberField
                                data-sim-cursor-key="cfg-settlement"
                                forceHover={demoHover("settlement")}
                                forceFocus={demoPress("settlement")}
                                value={settlementCycleLength}
                                onChange={(n) =>
                                    setSettlementCycleLength(
                                        n === null ? 1 : Math.max(1, n)
                                    )
                                }
                                ariaLabel="Settlement cycle length"
                                className="w-20 gap-0 px-2"
                            />
                            <Select
                                value={settlementCycleUnit}
                                onValueChange={(value) =>
                                    setSettlementCycleUnit(
                                        value === "weeks" ? "weeks" : "days"
                                    )
                                }
                            >
                                <SelectTrigger className="w-24">
                                    <SelectValue>
                                        {() =>
                                            settlementCycleUnit === "weeks"
                                                ? "Weeks"
                                                : "Days"
                                        }
                                    </SelectValue>
                                </SelectTrigger>
                                <SelectContent
                                    align="start"
                                    alignItemWithTrigger={false}
                                >
                                    <SelectItem value="days">Days</SelectItem>
                                    <SelectItem value="weeks">Weeks</SelectItem>
                                </SelectContent>
                            </Select>
                        </div>
                    </FieldRow>
                </div>
            </div>
        </Section>
    );
}
